import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Building2, TrendingUp, ChevronRight, BarChart3 } from 'lucide-react';
import { reportesApi, edificiosApi, cotizacionesApi, resultadosApi } from '../../lib/api';
import { formatCurrency, formatPeriodo, formatPercentage, getStatusLabel, cn } from '../../lib/utils';
import { StatusBadge } from '../../components/common/StatusBadge';
import { CurrencyToggle } from '../../components/common/CurrencyToggle';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

export const AdminReporteUnidades = () => {
  const [unidades, setUnidades] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [cotizaciones, setCotizaciones] = useState({});
  const [displayMoneda, setDisplayMoneda] = useState('ARS');
  const [filterEdificio, setFilterEdificio] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Detail dialog state
  const [selectedUnidad, setSelectedUnidad] = useState(null);
  const [unidadResultados, setUnidadResultados] = useState([]);
  const [loadingResultados, setLoadingResultados] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchUnidades();
  }, [filterEdificio]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [unidadesRes, edificiosRes, cotizacionesRes] = await Promise.all([
        reportesApi.unidades(),
        edificiosApi.list(),
        cotizacionesApi.list(),
      ]);
      
      setUnidades(unidadesRes.data);
      setEdificios(edificiosRes.data);
      
      const cotizacionesMap = {};
      cotizacionesRes.data.forEach(c => {
        cotizacionesMap[c.periodo] = c.usd_ars;
      });
      setCotizaciones(cotizacionesMap);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnidades = async () => {
    try {
      const params = filterEdificio && filterEdificio !== 'all' ? { edificio_id: filterEdificio } : {};
      const response = await reportesApi.unidades(params);
      setUnidades(response.data);
    } catch (err) {
      console.error('Error fetching unidades:', err);
    }
  };

  const handleOpenDetail = async (unidad) => {
    setSelectedUnidad(unidad);
    setIsDialogOpen(true);
    setLoadingResultados(true);
    
    try {
      const response = await resultadosApi.list({ unidad_id: unidad.unidad_id });
      setUnidadResultados(response.data);
    } catch (err) {
      console.error('Error fetching resultados:', err);
      toast.error('Error al cargar los resultados');
    } finally {
      setLoadingResultados(false);
    }
  };

  const getConvertedValue = (monto, moneda, periodo) => {
    if (!monto || moneda === displayMoneda) return monto;
    
    const cotizacion = cotizaciones[periodo];
    if (!cotizacion) return monto;
    
    if (moneda === 'ARS' && displayMoneda === 'USD') {
      return monto / cotizacion;
    } else if (moneda === 'USD' && displayMoneda === 'ARS') {
      return monto * cotizacion;
    }
    
    return monto;
  };

  if (loading) return <LoadingState message="Cargando datos..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Reporte por Unidad</h1>
          <p className="text-slate-500 mt-1">Rendimiento individual de cada unidad</p>
        </div>
        
        <CurrencyToggle value={displayMoneda} onChange={setDisplayMoneda} />
      </div>

      {/* Filter */}
      <div className="mb-6">
        <Select value={filterEdificio} onValueChange={setFilterEdificio}>
          <SelectTrigger className="w-full md:w-[300px]" data-testid="filter-edificio">
            <SelectValue placeholder="Filtrar por edificio" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los edificios</SelectItem>
            {edificios.map((e) => (
              <SelectItem key={e.edificio_id} value={e.edificio_id}>
                {e.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Units list */}
      {unidades.length === 0 ? (
        <EmptyState
          icon={Home}
          title="Sin unidades"
          description="No hay unidades para mostrar."
        />
      ) : (
        <div className="space-y-3">
          {unidades.map((unidad) => {
            const ganancia = unidad.ganancia_ultimo_mes;
            let gananciaDisplay = null;
            
            if (ganancia) {
              gananciaDisplay = getConvertedValue(
                ganancia.monto, 
                ganancia.moneda, 
                unidad.ultimo_periodo
              );
            }

            return (
              <div
                key={unidad.unidad_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors duration-200 cursor-pointer"
                onClick={() => handleOpenDetail(unidad)}
                data-testid={`unidad-card-${unidad.unidad_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">{unidad.edificio_nombre}</span>
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 mb-2">{unidad.nombre_unidad}</h3>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={unidad.estado_alquiler} />
                      <span className="text-sm text-slate-500">{getStatusLabel(unidad.tipo)}</span>
                      {unidad.ultimo_periodo && (
                        <span className="text-sm text-slate-500">
                          • Últ: {formatPeriodo(unidad.ultimo_periodo)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      {gananciaDisplay !== null && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Ganancia</p>
                          <p className={cn(
                            "text-lg font-bold font-mono",
                            gananciaDisplay >= 0 ? "text-green-700" : "text-red-700"
                          )}>
                            {formatCurrency(gananciaDisplay, displayMoneda)}
                          </p>
                        </div>
                      )}
                      {unidad.rentabilidad_anual_proyectada_pct && (
                        <p className="text-sm text-slate-500 mt-1">
                          Rent: {formatPercentage(unidad.rentabilidad_anual_proyectada_pct)}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUnidad?.nombre_unidad} - {selectedUnidad?.edificio_nombre}
            </DialogTitle>
          </DialogHeader>
          
          {loadingResultados ? (
            <LoadingState message="Cargando resultados..." />
          ) : unidadResultados.length > 0 ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Estado
                  </p>
                  <StatusBadge status={selectedUnidad?.estado_alquiler} />
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Tipo
                  </p>
                  <p className="font-medium text-slate-900">{getStatusLabel(selectedUnidad?.tipo)}</p>
                </div>
              </div>

              {/* Evolution table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Evolución Mensual</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Período
                        </th>
                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Ingreso
                        </th>
                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Egreso
                        </th>
                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Fee
                        </th>
                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Ganancia
                        </th>
                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                          Rent. %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unidadResultados.map((r) => {
                        const ganancia = r.usa_manual && r.ganancia_neta_manual
                          ? r.ganancia_neta_manual
                          : r.ganancia_neta_calculada;
                        
                        return (
                          <tr 
                            key={r.resultado_id}
                            className="border-b border-slate-100"
                          >
                            <td className="py-3 px-4 font-medium text-slate-900">
                              {formatPeriodo(r.periodo)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-slate-700">
                              {formatCurrency(
                                getConvertedValue(r.ingreso?.monto, r.ingreso?.moneda, r.periodo),
                                displayMoneda
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-red-700">
                              {formatCurrency(
                                getConvertedValue(r.egreso?.monto, r.egreso?.moneda, r.periodo),
                                displayMoneda
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-slate-700">
                              {formatCurrency(
                                getConvertedValue(r.fee_administracion?.monto, r.fee_administracion?.moneda, r.periodo),
                                displayMoneda
                              )}
                            </td>
                            <td className={cn(
                              "py-3 px-4 text-right font-mono font-semibold",
                              ganancia?.monto >= 0 ? "text-green-700" : "text-red-700"
                            )}>
                              {formatCurrency(
                                getConvertedValue(ganancia?.monto, ganancia?.moneda, r.periodo),
                                displayMoneda
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-slate-700">
                              {formatPercentage(r.rentabilidad_anual_proyectada_pct)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={BarChart3}
              title="Sin resultados"
              description="Esta unidad no tiene resultados publicados."
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReporteUnidades;
